import { cn } from "@/lib/utils";
import {
  BarChart,
  Hash,
  LucideIcon,
  Phone,
  TextCursor,
  Users,
  Settings,
} from "lucide-react";
import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";

interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  badge?: number;
}

const navItems: NavItem[] = [
  { href: "/calls", label: "Calls", icon: Phone },
  { href: "/strategies", label: "Strategies", icon: BarChart },
  { href: "/settings", label: "Settings", icon: Settings },
];

const adminNavItems: NavItem[] = [
  { href: "/admin/organization", label: "Organization Users", icon: Users },
  { href: "/admin/numbers", label: "Verified Phone Numbers", icon: Hash },
  {
    href: "/admin/main-system-prompt",
    label: "Main System Prompt",
    icon: TextCursor,
  },
  { href: "/test-gen-basic", label: "Dialog Simulation Lab", icon: TextCursor },
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);

  // Get the returnUrl from location state if we're on a call detail page
  const callsHref = useMemo(() => {
    if (location.pathname.startsWith("/calls/") && location.state?.returnUrl) {
      return location.state.returnUrl;
    }
    return "/calls";
  }, [location.pathname, location.state]);

  const renderNavItems = (items: NavItem[]) =>
    items.map((item) => {
      // Use the preserved URL for the Calls link
      const href = item.href === "/calls" ? callsHref : item.href;
      
      return (
        <Link
          key={item.href}
          to={href}
          className={cn(
            "flex items-center gap-3 rounded-lg px-3 py-2 text-gray-500 border transition-all hover:text-gray-900",
            location.pathname === item.href || (item.href === "/calls" && location.pathname.startsWith("/calls"))
              ? "bg-muted border-slate-200 text-gray-900"
              : "border-transparent"
          )}
        >
          <item.icon className="h-4 w-4" />
          {item.label}
          {item.badge && (
            <span className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-900">
              {item.badge}
            </span>
          )}
        </Link>
      );
    });

  return (
    <div className="hidden border-r bg-muted/40 md:block">
      <div className="flex h-full max-h-screen flex-col gap-2">
        <div className="flex h-[60px] items-center border-b px-6">
          <Link to="/" className="flex items-center gap-2 font-semibold">
            <img
              src={require("@/assets/pear_logo.svg").default}
              alt="Pear Healthcare"
              className="h-8 w-20"
            />
          </Link>
        </div>
        <div className="flex-1 overflow-auto py-2">
          <nav className="grid items-start px-4 text-sm font-medium">
            {renderNavItems(navItems)}
            {user?.is_superuser && (
              <>
                <div className="mt-6 mb-2 px-3 text-xs font-semibold text-gray-500">
                  Admin
                </div>
                {renderNavItems(adminNavItems)}
              </>
            )}
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
